import React, { useEffect, useState } from 'react';
import ApproveApplicantModal from '@/components/ApproveApplicantModal';
import RejectUserModal from '@/components/RejectUserModal';
import { useToast } from '@/components/Toast';
import { userApprovalService, type PendingUser } from '@/api/services/userApproval';
import { formatApiError } from '@/utils/formatApiError';
import { DataTableScrollArea } from '@/components/table';

export default function ApprovalsPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<PendingUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [approveTarget, setApproveTarget] = useState<PendingUser | null>(null);
  const [approveSubmitting, setApproveSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await userApprovalService.listPending());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pending users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const executeApprove = async (payload: {
    organizationId?: string;
    organizationName?: string;
    organizationLabel: string;
    membershipRole: string;
    planSlug?: string;
  }) => {
    if (!approveTarget) return;
    setApproveSubmitting(true);
    try {
      await userApprovalService.approve(approveTarget.id, {
        organizationId: payload.organizationId,
        organizationName: payload.organizationName,
        membershipRole: payload.membershipRole,
        planSlug: payload.planSlug,
      });
      toast(`Applicant approved and added to ${payload.organizationLabel}. Approval email queued.`, 'success');
      setApproveTarget(null);
      await load();
    } catch (e) {
      toast(formatApiError(e), 'error');
      throw e;
    } finally {
      setApproveSubmitting(false);
    }
  };

  const executeReject = async (payload: {
    internalNote?: string;
    customerMessage?: string;
    includeCustomerMessage: boolean;
  }) => {
    if (!rejectTarget) return;
    try {
      await userApprovalService.reject(rejectTarget, {
        internalNote: payload.internalNote,
        customerMessage: payload.customerMessage,
        includeCustomerMessage: payload.includeCustomerMessage,
      });
      toast('User rejected. Confirmation email queued.', 'success');
      await load();
    } catch (e) {
      toast(formatApiError(e), 'error');
    } finally {
      setRejectTarget(null);
    }
  };

  if (loading) return <div className="panel">Loading pending approvals…</div>;

  return (
    <div className="panel">
      <h1>Pending approvals</h1>
      {error ? <div className="alert alert-error">{error}</div> : null}
      {rows.length === 0 ? (
        <p className="muted">No users waiting for approval.</p>
      ) : (
        <DataTableScrollArea>
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Registered</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.email}</td>
                <td>{row.name}</td>
                <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}</td>
                <td>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setApproveTarget(row)}>
                    Approve
                  </button>{' '}
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRejectTarget(row.id)}>
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </DataTableScrollArea>
      )}
      <ApproveApplicantModal
        applicant={approveTarget}
        open={Boolean(approveTarget)}
        submitting={approveSubmitting}
        onCancel={() => setApproveTarget(null)}
        onConfirm={executeApprove}
      />
      <RejectUserModal
        open={Boolean(rejectTarget)}
        onCancel={() => setRejectTarget(null)}
        onConfirm={executeReject}
      />
    </div>
  );
}
